/**
 * Hero Carousel - Mobile-First with Swipe Gestures
 * Similar to Concept Theme implementation
 */

class HeroCarousel {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('[data-carousel-track]');
    this.slides = [...container.querySelectorAll('[data-carousel-slide]')];
    this.prevBtn = container.querySelector('[data-carousel-prev]');
    this.nextBtn = container.querySelector('[data-carousel-next]');
    this.pagination = container.querySelector('[data-carousel-pagination]');
    this.dots = this.pagination ? [...this.pagination.querySelectorAll('.pagination-dot')] : [];

    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.isTransitioning = false;
    this.autoplayInterval = null;

    // Touch/Swipe properties
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.touchStartY = 0;
    this.isDragging = false;
    this.dragThreshold = 50; // Minimum drag distance to trigger slide change

    // Get settings from data attributes or defaults
    const wrapper = container.closest('[data-carousel-wrapper]');
    const section = wrapper ? wrapper.closest('.section-hero-carousel') : null;

    this.settings = {
      autoplay: this.getSettingValue('autoplay', true),
      autoplaySpeed: this.getSettingValue('autoplay_speed', 5) * 1000,
      enableSwipe: this.getSettingValue('enable_swipe', true)
    };

    this.init();
  }

  getSettingValue(key, defaultValue) {
    // Try to get from section settings (would need to be passed via data attribute)
    const wrapper = this.container.closest('[data-carousel-wrapper]');
    if (wrapper && wrapper.dataset[key]) {
      return wrapper.dataset[key] === 'true';
    }
    return defaultValue;
  }

  init() {
    if (this.totalSlides <= 1) return;

    this.setupEventListeners();
    this.updateSlide(0, false);

    if (this.settings.autoplay) {
      this.startAutoplay();
    }

    // Pause autoplay on hover (desktop)
    this.container.addEventListener('mouseenter', () => this.stopAutoplay());
    this.container.addEventListener('mouseleave', () => {
      if (this.settings.autoplay) this.startAutoplay();
    });
  }

  setupEventListeners() {
    // Arrow navigation
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }

    // Dot navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    // Touch/Swipe events for mobile
    if (this.settings.enableSwipe) {
      this.track.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
      this.track.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
      this.track.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });

      // Mouse drag for desktop
      this.track.addEventListener('mousedown', (e) => this.handleMouseDown(e));
      this.track.addEventListener('mousemove', (e) => this.handleMouseMove(e));
      this.track.addEventListener('mouseup', (e) => this.handleMouseUp(e));
      this.track.addEventListener('mouseleave', (e) => this.handleMouseUp(e));
    }

    // Keyboard navigation
    this.container.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.prev();
      if (e.key === 'ArrowRight') this.next();
    });
  }

  // Touch Handlers (Mobile Swipe)
  handleTouchStart(e) {
    this.touchStartX = e.touches[0].clientX;
    this.touchStartY = e.touches[0].clientY;
    this.stopAutoplay();
  }

  handleTouchMove(e) {
    if (!this.touchStartX) return;

    const touchCurrentX = e.touches[0].clientX;
    const touchCurrentY = e.touches[0].clientY;

    const diffX = Math.abs(this.touchStartX - touchCurrentX);
    const diffY = Math.abs(this.touchStartY - touchCurrentY);

    // If horizontal swipe is more significant than vertical, prevent page scroll
    if (diffX > diffY) {
      e.preventDefault();
    }
  }

  handleTouchEnd(e) {
    this.touchEndX = e.changedTouches[0].clientX;
    this.handleSwipe();

    if (this.settings.autoplay) {
      this.startAutoplay();
    }
  }

  // Mouse Drag Handlers (Desktop)
  handleMouseDown(e) {
    this.isDragging = true;
    this.touchStartX = e.clientX;
    this.track.classList.add('dragging');
    this.track.style.cursor = 'grabbing';
    this.stopAutoplay();
  }

  handleMouseMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();
  }

  handleMouseUp(e) {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.touchEndX = e.clientX;
    this.track.classList.remove('dragging');
    this.track.style.cursor = '';
    this.handleSwipe();

    if (this.settings.autoplay) {
      this.startAutoplay();
    }
  }

  handleSwipe() {
    const diff = this.touchStartX - this.touchEndX;

    if (Math.abs(diff) > this.dragThreshold) {
      if (diff > 0) {
        // Swipe left - next slide
        this.next();
      } else {
        // Swipe right - prev slide
        this.prev();
      }
    }

    // Reset
    this.touchStartX = 0;
    this.touchEndX = 0;
  }

  prev() {
    if (this.isTransitioning) return;
    const newIndex = this.currentIndex === 0 ? this.totalSlides - 1 : this.currentIndex - 1;
    this.goToSlide(newIndex);
  }

  next() {
    if (this.isTransitioning) return;
    const newIndex = this.currentIndex === this.totalSlides - 1 ? 0 : this.currentIndex + 1;
    this.goToSlide(newIndex);
  }

  goToSlide(index, animate = true) {
    if (this.isTransitioning || index === this.currentIndex) return;

    this.updateSlide(index, animate);
  }

  updateSlide(index, animate = true) {
    this.isTransitioning = true;
    this.currentIndex = index;

    // Add transitioning class for smooth animation
    if (animate) {
      this.track.classList.add('transitioning');
    }

    // Move track
    const offset = -index * 100;
    this.track.style.transform = `translateX(${offset}%)`;

    // Update dots
    this.updateDots();

    // Update aria labels for accessibility
    this.slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i !== index);
    });

    // Reset transitioning state
    setTimeout(() => {
      this.isTransitioning = false;
      if (animate) {
        this.track.classList.remove('transitioning');
      }
    }, 600);
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      if (index === this.currentIndex) {
        dot.classList.add('active');
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.classList.remove('active');
        dot.setAttribute('aria-current', 'false');
      }
    });
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => {
      this.next();
    }, this.settings.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  destroy() {
    this.stopAutoplay();
    // Remove event listeners if needed
  }
}

// Initialize all carousels on the page
function initCarousels() {
  const carousels = document.querySelectorAll('[data-carousel]');
  carousels.forEach(carousel => {
    new HeroCarousel(carousel);
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCarousels);
} else {
  initCarousels();
}

// Re-initialize on section render (Shopify theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', initCarousels);
}
